import { useEffect, useState } from "react";
import { Copy, Plus, Search } from "lucide-react";
import { useSkillLibrary, type LibrarySkill } from "~/components/assistant/hooks/useSkillLibrary";
import { Badge } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";

type LibraryFilter = "all" | "system" | "user";

const FILTER_LABELS: Record<LibraryFilter, string> = {
  all: "All",
  system: "System",
  user: "User",
};

interface SkillPickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (skill: LibrarySkill) => void;
  onCreateNew: () => void;
}

const SkillPickerModal = ({ open, onClose, onPick, onCreateNew }: SkillPickerModalProps) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<LibraryFilter>("all");

  const { skills: library, isLoading: libraryLoading } = useSkillLibrary(open);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setFilter("all");
    }
  }, [open]);

  const needle = search.trim().toLowerCase();
  const filtered = library
    .filter((s) => {
      if (filter === "system" && s.origin !== "system") {
        return false;
      }
      if (filter === "user" && s.origin !== "user") {
        return false;
      }
      if (!needle) {
        return true;
      }
      return (
        s.name.toLowerCase().includes(needle) ||
        (s.description?.toLowerCase().includes(needle) ?? false)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="flex h-[680px] max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:w-[70vw] sm:max-w-[920px]">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle>Add a skill</DialogTitle>
          <DialogDescription>Browse the library or write your own from scratch.</DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-4 px-6 pb-3">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              placeholder="Search the library…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label="Search the library"
            />
          </div>
          <LibraryFilterToggle filter={filter} setFilter={setFilter} />
        </div>

        <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-6 pb-6">
          <PickerRow
            icon={<Plus className="size-4" />}
            title="Create a new skill"
            description="Start from a blank template and write the instructions yourself."
            onClick={onCreateNew}
          />

          <div className="mt-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Library
              {!libraryLoading && (
                <span className="ml-1 font-normal text-muted-foreground/70">
                  ({filtered.length})
                </span>
              )}
            </span>
          </div>

          {libraryLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {needle ? "No skills match your search." : "No skills in your library yet."}
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {filtered.map((skill) => (
                <li key={skill.id}>
                  <PickerRow
                    icon={<Copy className="size-4" />}
                    title={skill.name}
                    description={skill.description}
                    onClick={() => onPick(skill)}
                    trailing={
                      <Badge
                        variant={skill.origin === "system" ? "default" : "muted"}
                        className="shrink-0 px-1.5 py-0 text-[0.625rem] uppercase tracking-wide"
                      >
                        {skill.origin === "system" ? "System" : "User"}
                      </Badge>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface PickerRowProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  trailing?: React.ReactNode;
}

const PickerRow = ({ icon, title, description, onClick, trailing }: PickerRowProps) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-left text-foreground transition-colors hover:bg-accent"
  >
    <div className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{title}</p>
      {description && <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>}
    </div>
    {trailing}
  </button>
);

interface LibraryFilterToggleProps {
  filter: LibraryFilter;
  setFilter: (filter: LibraryFilter) => void;
}

const LibraryFilterToggle = ({ filter, setFilter }: LibraryFilterToggleProps) => (
  <ToggleGroup
    type="single"
    variant="outline"
    size="sm"
    value={filter}
    onValueChange={(next) => {
      // Radix returns "" when the user re-clicks the active item. Treat that as no-op
      // (the filter should always have a selection).
      if (next) {
        setFilter(next as LibraryFilter);
      }
    }}
    aria-label="Filter library"
  >
    {(Object.keys(FILTER_LABELS) as LibraryFilter[]).map((key) => (
      <ToggleGroupItem key={key} value={key} className="h-8 px-4 text-xs font-medium">
        {FILTER_LABELS[key]}
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);

export default SkillPickerModal;
